/* Gli indicatori di gravita' e di margine (§ 3.2, § 3.3).
 *
 * Stanno insieme perche' si leggono insieme: il rango dice quanto e' grave, il
 * margine dice di quanto si sfora. Separati, l'operatore li cercherebbe in due
 * punti diversi dello schermo per ogni riga della coda.
 */
import { decimal, margin as writeMargin } from '../format'
import { bandOf, marginOf, rankOf } from './scales'

/** I livelli della Specifica Tecnica, dal piu' grave: e' l'ordine in cui si
 *  cercano. */
const LEVELS = [1, 2, 3]

/** Il rango: tono, glifo e numero. Mai uno dei tre da solo. */
export function Severity({ level }: { level: number | null }): JSX.Element {
  const rank = rankOf(level)
  return (
    <span
      className="grado"
      style={{ color: `var(--${rank.tone})` }}
      // Il significato d'esercizio non sta scritto in ogni riga — sarebbero
      // ottocento ripetizioni — ma dev'essere raggiungibile senza chiedere.
      title={rank.meaning}
    >
      <span className="glifo" aria-hidden="true">
        {rank.glyph}
      </span>
      {rank.name}
      <span className="solo-lettori"> · {rank.meaning}</span>
    </span>
  )
}

/**
 * Quanti per livello, dal piu' grave. Il numero da solo — «12 da verificare» —
 * non dice se sono dodici cose da fare entro 48 ore o dodici da programmare:
 * accanto al numero sta il rango di ciascuno, con tono, glifo e nome, e i
 * livelli a zero non compaiono. Con nessun livello contato non si disegna
 * niente: uno spazio vuoto dice «niente da dire» meglio di tre zeri.
 */
export function Levels({ counts }: { counts: Record<string, number> | undefined }): JSX.Element | null {
  const shown = LEVELS.filter((level) => (counts?.[String(level)] ?? 0) > 0)
  if (shown.length === 0) return null
  return (
    <span className="gradi">
      {shown.map((level) => (
        <span key={level} className="gradi-voce">
          <Severity level={level} />
          <span className="t-num">{decimal(counts?.[String(level)] ?? 0)}</span>
        </span>
      ))}
    </span>
  )
}

/**
 * Il margine sulla norma: `Δ = misurata − minima`.
 *
 * Si mostra il margine e non la distanza perche' una distanza assoluta, fuori
 * dal suo contesto, non si sa leggere: la soglia dipende dalla tensione della
 * linea e da che cosa c'e' sotto il conduttore.
 */
export function Margin({
  measured,
  minimum,
}: {
  measured: number | null
  minimum: number | null
}): JSX.Element {
  const delta = marginOf(measured, minimum)
  const band = bandOf(delta)
  return (
    <span
      className={band.unmeasured ? 'margine non-misurato' : 'margine'}
      style={{ color: `var(--${band.tone})` }}
      title={band.meaning}
    >
      {writeMargin(delta)}
      <span className="solo-lettori"> · {band.meaning}</span>
    </span>
  )
}
